import { useState, useEffect, useCallback, useRef } from 'react';
import { syncManager } from '../services/syncManager';
import { offlineStorage } from '../services/offlineStorage';

/**
 * Hook for managing offline status and sync
 */
export function useOfflineSync() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [pendingChanges, setPendingChanges] = useState(0);

  useEffect(() => {
    // Subscribe to sync manager events
    const unsubscribe = syncManager.addListener((event) => {
      switch (event.type) {
        case 'online':
          setIsOnline(true);
          break;
        case 'offline':
          setIsOnline(false);
          break;
        case 'sync_started':
          setIsSyncing(true);
          break;
        case 'sync_complete':
          setIsSyncing(false);
          setSyncStatus({
            success: true,
            synced: event.synced,
            failed: event.failed,
            timestamp: new Date()
          });
          updatePendingCount();
          break;
        case 'sync_error':
          setIsSyncing(false);
          setSyncStatus({
            success: false,
            error: event.error,
            timestamp: new Date()
          });
          break;
        case 'change_saved':
          updatePendingCount();
          break;
        default:
          break;
      }
    });

    // Initial status check
    updatePendingCount();

    return unsubscribe;
  }, []);

  const updatePendingCount = async () => {
    const stats = await offlineStorage.getStorageStats();
    setPendingChanges(stats.pending_changes);
  };

  const forceSync = useCallback(async () => {
    if (isOnline) {
      await syncManager.forcSync();
    }
  }, [isOnline]);

  return {
    isOnline,
    isSyncing,
    syncStatus,
    pendingChanges,
    forceSync
  };
}

/**
 * Hook for fetching contracts with offline support
 */
export function useOfflineContracts() {
  const [contracts, setContracts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [source, setSource] = useState(null); // 'server' or 'cache'
  const [error, setError] = useState(null);
  const { isOnline } = useOfflineSync();

  const fetchContracts = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await syncManager.fetchContracts();
      setContracts(result.data);
      setSource(result.source);
    } catch (err) {
      setError(err.message);
      // Try to get from cache on error
      try {
        const cached = await offlineStorage.getAllContracts();
        setContracts(cached);
        setSource('cache');
      } catch (cacheErr) {
        console.error('Cache fetch also failed:', cacheErr);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  // Refetch when coming back online
  useEffect(() => {
    if (isOnline && source === 'cache') {
      fetchContracts();
    }
  }, [isOnline, source, fetchContracts]);

  return {
    contracts,
    isLoading,
    source,
    error,
    refetch: fetchContracts
  };
}

/**
 * Hook for fetching a single contract with offline support
 */
export function useOfflineContract(contractId) {
  const [contract, setContract] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [source, setSource] = useState(null);
  const [error, setError] = useState(null);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const { isOnline } = useOfflineSync();

  const fetchContract = useCallback(async () => {
    if (!contractId) return;
    
    setIsLoading(true);
    setError(null);

    try {
      const result = await syncManager.fetchContract(contractId);
      setContract(result.data);
      setSource(result.source);
      setHasPendingChanges(result.data?.sync_status === 'pending');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [contractId]);

  useEffect(() => {
    fetchContract();
  }, [fetchContract]);

  // Refetch when coming back online
  useEffect(() => {
    if (isOnline && source === 'cache') {
      fetchContract();
    }
  }, [isOnline, source, fetchContract]);

  const saveOffline = useCallback(async (content, title) => {
    await syncManager.saveContractOffline(contractId, content, title);
    setHasPendingChanges(true);
    
    // Update local state
    setContract(prev => ({
      ...prev,
      content,
      ...(title && { title }),
      sync_status: 'pending'
    }));
  }, [contractId]);

  return {
    contract,
    isLoading,
    source,
    error,
    hasPendingChanges,
    saveOffline,
    refetch: fetchContract
  };
}

/**
 * Hook for auto-save with offline support
 */
export function useOfflineAutoSave(contractId, content, delay = 2000) {
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const timeoutRef = useRef(null);
  const lastContentRef = useRef(content);
  const { isOnline } = useOfflineSync();

  useEffect(() => {
    // Skip if content hasn't changed
    if (content === lastContentRef.current) return;
    lastContentRef.current = content;

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout for auto-save
    timeoutRef.current = setTimeout(async () => {
      if (!contractId || !content) return;

      setIsSaving(true);
      try {
        await syncManager.saveContractOffline(contractId, content);
        setLastSaved(new Date());
      } catch (err) {
        console.error('Auto-save failed:', err);
      } finally {
        setIsSaving(false);
      }
    }, delay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [content, contractId, delay]);

  return {
    isSaving,
    lastSaved,
    isOnline
  };
}
